import { Component, OnInit, Input, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { FlyToObject } from 'src/app/engine/state/engine.actions';
import { Store, Select } from '@ngxs/store';
import { SetObjectLayerOrder } from 'src/app/engine/state/engine.actions';
import { EditState } from 'src/app/edit/state/edit.state';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { BdGraph } from 'src/lib/graph/graph.model';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import {
  CollapseNode,
  ExpandNode,
  CheckNode,
  ToggleVisibleNodes,
  SetCheckedNodes,
} from 'src/app/engine/state/nodes/engine.nodes.actions';
import { TreeTheme } from '../tree/tree.component';
import { environment } from 'src/environments/environment';
import { GraphUtils } from 'src/lib/graph/graph-utils';

@Component({
  selector: 'app-tree-nested-node',
  templateUrl: './tree-nested-node.component.html',
  styleUrls: ['./tree-nested-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})

export class TreeNestedNodeComponent implements OnInit, OnDestroy {
  treeTheme = TreeTheme;
  isLoft = environment.exportType === 'module';

  @Select(EngineNodesState.getCheckedNodeIds)
  checkedNodesIds$: Observable<string[]>;

  @Select(EditState.getLayerOrderMode)
  layerOrderMode$: Observable<boolean>;

  @Input()
  graph: BdGraph<ViewTreeNode>;

  @Input()
  public get node(): ViewTreeNode {
    return this._node;
  }
  public set node(value: ViewTreeNode) {
    this._node = value;
    this.sanitizedID = this.graphService.sanitizeNodeID(this.node.id);
    
    this.checked = this.isChecked(this.node);

    this.hasChildren = GraphUtils.getSuccessors(this.graph, this.node.id).length > 0;

    this.changeDetection.markForCheck();
  }
  private _node: ViewTreeNode;

  @Input()
  nested = true;

  @Input()
  theme: TreeTheme = TreeTheme.Dark;

  checked = false;
  hasChildren = false;
  

  // DOM friendly version of node ID
  // nodeID may include pipe characters, which cause errors with querySelector
  sanitizedID = '';

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private changeDetection: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.checkedNodesIds$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(nodeIds => {
        this.checked = nodeIds.some((id) => id === this.node.id);
        this.changeDetection.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleExpandClick(node: ViewTreeNode, expanded: boolean) {
    (expanded) ?
      this.store.dispatch(new CollapseNode(node)) :
      this.store.dispatch(new ExpandNode(node));
  }
  
  handleNodeClick(event: MouseEvent, node: ViewTreeNode) {
    if (event.shiftKey) {
      this.checkNode(node);
    } else {
      this.setCheckedNodes(node);
      this.store.dispatch(new FlyToObject(node.id));

      this.handleExpandClick(node, false);
    }
  }

  toggleVisible(node: ViewTreeNode, show?: boolean) {
    show = (typeof show === 'undefined') ? !node.boolState.shown : show;
    this.store.dispatch(new ToggleVisibleNodes([node], show));
    this.changeDetection.markForCheck();
  }
  
  handleLayerOrder(event: Event, node: ViewTreeNode) {
    if (
      (event instanceof KeyboardEvent && event.key.toLowerCase() === 'enter') ||
      event instanceof FocusEvent
    ) {
      const eventTarget = event.target as HTMLInputElement;
      const layer = parseInt(eventTarget.value, 10);

      this.setLayerOrder(node, layer);
    }
  }

  isChecked(node) {
    return this.store.selectSnapshot(EngineNodesState.getCheckedNodes)
      .some(checkedNode => checkedNode.id === node.id);
  }

  checkNode(node: ViewTreeNode, checked?: boolean) {
    if (typeof checked === 'undefined') {
      // When not explicitly defiend, toggle the node checked state
      checked = !this.isChecked(node);
    }

    this.store.dispatch(new CheckNode(node, checked));
  }


  setCheckedNodes(node: ViewTreeNode, checked?: boolean) {
    if (typeof checked === 'undefined') {
      // When not explicitly defiend, toggle the node checked state
      checked = !this.isChecked(node);
    }

    const nodes = checked ? [node] : [];
    this.store.dispatch(new SetCheckedNodes(nodes));
  }

  setLayerOrder(node: ViewTreeNode, layer: number) {
    this.store.dispatch(new SetObjectLayerOrder([node], layer));
    this.changeDetection.markForCheck();
  }
}

